import { IconArrowLeft, IconCirclePlus, IconTrash } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import {
  BasicDatePicker,
  BasicTable,
  Loading,
  SingleBranchSelect,
  TreeSelector,
  TreeSelectorChild,
  TreeSelectorParent,
} from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import type { KPIOption } from '@/api/kpi'
import type { ScalrTableColumn } from '@/components/customComponents'
import { toast, useKpiGoalAssignments } from '@/hooks'
import { TranslationFunctionType } from '@/i18n'
import { KpiAssignmentCreationModel } from '@/types'
import { DateType } from '@/utils/date'

interface Selectable {
  id: string
  name: string
}

type AdditionSelections = {
  branch?: string // Branch name from SingleBranchSelect
  dateRange?: DateRange
  dateFilterType?: DateType
  kpi?: Selectable
}

interface CreateGoalPlanProps {
  onCancel: () => void
  onSave: () => void
  options: (KPIOption & Selectable)[]
}

interface KpiAssignmentData {
  id: number
  user: number
  branchName: string
  kpiMetric: string
  year: number
  quarter: number
  targetValue: number
  bonusAmount: number
  // Display fields
  userName: string
  kpiName: string
}

const getKpiAddFormColumns = (
  t: TranslationFunctionType
): ScalrTableColumn[] => [
  {
    accessorKey: 'branch',
    header: t('targets.table.branch'),
    type: 'text',
  },
  {
    accessorKey: 'kpiName',
    header: t('targets.table.kpiName'),
    type: 'text',
  },
  {
    accessorKey: 'user',
    header: t('targets.table.user'),
    type: 'text',
  },
  {
    accessorKey: 'date',
    header: t('targets.table.date'),
    type: 'text',
  },
  {
    accessorKey: 'target',
    header: t('targets.table.target'),
    type: 'number',
  },
  {
    accessorKey: 'bonus',
    header: t('targets.table.bonus'),
    type: 'number',
  },
]

export const CreateGoalPlan = ({
  onCancel,
  onSave,
  options: _options,
}: CreateGoalPlanProps) => {
  const { t } = useTranslation()
  const [additionSelections, setAdditionSelections] =
    useState<AdditionSelections>({
      branch: undefined,
      dateRange: undefined,
      dateFilterType: 'quarterly',
      kpi: undefined,
    })
  const [targetValue, setTargetValue] = useState(0)
  const [bonusAmount, setBonusAmount] = useState(0)
  const [kpiAssignments, setKpiAssignments] = useState<KpiAssignmentData[]>([])
  const [selectedKpiId, setSelectedKpiId] = useState<string>()
  const [selectedUserId, setSelectedUserId] = useState<string>()

  const { fetchMetadata, createAssignments, metadata, loading, error } =
    useKpiGoalAssignments()

  // Show error toast if there is an error
  useEffect(() => {
    if (error) {
      toast({
        title: t('clarity.errors.somethingWentWrong'),
        description: error.message,
        variant: 'destructive',
      })
    }
  }, [error])

  // Transform metadata.availableKpis to TreeSelector format
  const kpiTreeData: TreeSelectorParent[] = useMemo(() => {
    if (!metadata?.availableKpis) return []

    return Object.entries(metadata.availableKpis).map(([category, kpis]) => ({
      id: category,
      label: category,
      children: kpis.map((kpi) => ({
        id: kpi.metricField,
        label: `${kpi.name}${kpi.unit ? ` (${kpi.unit})` : ''}`,
        disabled: false,
      })),
    }))
  }, [metadata])

  // Transform metadata.eligibleUsers to flat list format
  const userFlatData: TreeSelectorChild[] = useMemo(() => {
    if (!metadata?.eligibleUsers) return []

    return metadata.eligibleUsers.map((user) => ({
      id: user.id.toString(),
      label: user.fullName || user.username,
      disabled: false,
    }))
  }, [metadata])

  // Validate if all required fields are filled
  const isFormValid = useMemo(() => {
    return (
      !!additionSelections.branch &&
      !!selectedKpiId &&
      !!selectedUserId &&
      !!additionSelections.dateRange?.from &&
      targetValue > 0 &&
      bonusAmount > 0
    )
  }, [
    additionSelections.branch,
    additionSelections.dateRange,
    selectedKpiId,
    selectedUserId,
    targetValue,
    bonusAmount,
  ])

  const handleAddKPI = useCallback(() => {
    const selectedBranchName = additionSelections.branch
    const dateRange = additionSelections.dateRange

    if (
      !selectedBranchName ||
      !selectedKpiId ||
      !selectedUserId ||
      !dateRange?.from ||
      !metadata ||
      targetValue <= 0 ||
      bonusAmount <= 0
    ) {
      return
    }

    // Find user from metadata
    const selectedUser = metadata.eligibleUsers.find(
      (user) => user.id.toString() === selectedUserId
    )
    if (!selectedUser) {
      return
    }

    // Find KPI from metadata
    let selectedKpi: { metricField: string; name: string } | undefined
    for (const kpis of Object.values(metadata.availableKpis)) {
      const kpi = kpis.find((k) => k.metricField === selectedKpiId)
      if (kpi) {
        selectedKpi = { metricField: kpi.metricField, name: kpi.name }
        break
      }
    }
    if (!selectedKpi) {
      return
    }

    // Extract year and quarter from dateRange
    const year = dateRange.from.getFullYear()
    const quarter = Math.floor(dateRange.from.getMonth() / 3) + 1

    const newAssignment: KpiAssignmentData = {
      id: kpiAssignments.length + 1,
      user: selectedUser.id,
      branchName: selectedBranchName,
      kpiMetric: selectedKpi.metricField,
      year,
      quarter,
      targetValue,
      bonusAmount,
      // Display fields
      userName: selectedUser.fullName || selectedUser.username,
      kpiName: selectedKpi.name,
    }
    setKpiAssignments((prev) => [...prev, newAssignment])

    // Reset form fields after adding
    setSelectedKpiId(undefined)
    setSelectedUserId(undefined)
    setTargetValue(0)
    setBonusAmount(0)
    setAdditionSelections((prev) => ({
      ...prev,
      dateRange: undefined,
    }))
  }, [
    kpiAssignments,
    additionSelections.branch,
    additionSelections.dateRange,
    selectedKpiId,
    selectedUserId,
    targetValue,
    bonusAmount,
    metadata,
  ])

  const handleRemoveKPI = useCallback((id: number | string) => {
    setKpiAssignments((prev) =>
      prev.filter((assignment) => assignment.id !== id)
    )
  }, [])

  const resetAdditionForm = useCallback(() => {
    setKpiAssignments([])
    setAdditionSelections({
      branch: undefined,
      dateRange: undefined,
      dateFilterType: 'quarterly',
      kpi: undefined,
    })
    setTargetValue(0)
    setBonusAmount(0)
    setSelectedKpiId(undefined)
    setSelectedUserId(undefined)
  }, [])

  const handleCancel = useCallback(() => {
    const hasChanges =
      additionSelections.branch ||
      selectedKpiId ||
      selectedUserId ||
      additionSelections.dateRange ||
      kpiAssignments.length > 0 ||
      targetValue > 0 ||
      bonusAmount > 0

    if (hasChanges) {
      const confirmClose = window.confirm(t('targets.form.closeWithoutSaving'))
      if (confirmClose) {
        resetAdditionForm()
        onCancel()
      }
    } else {
      resetAdditionForm()
      onCancel()
    }
  }, [
    additionSelections.branch,
    additionSelections.dateRange,
    selectedKpiId,
    selectedUserId,
    kpiAssignments,
    targetValue,
    bonusAmount,
    resetAdditionForm,
    onCancel,
    t,
  ])

  const handleSave = useCallback(async () => {
    if (kpiAssignments.length === 0) {
      alert(t('targets.form.addAtLeastOneKpi'))
      return
    }

    // Map kpiAssignments to the API format (KpiAssignmentCreationModel)
    const assignmentsToSave: KpiAssignmentCreationModel[] = kpiAssignments.map(
      (assignment) => ({
        user: assignment.user,
        branch_name: assignment.branchName,
        kpiMetric: assignment.kpiMetric,
        year: assignment.year,
        quarter: assignment.quarter,
        targetValue: assignment.targetValue,
        bonusAmount: assignment.bonusAmount,
      })
    )

    try {
      await createAssignments(assignmentsToSave)
      console.log('Successfully saved KPI assignments')
      resetAdditionForm()
      onSave()
    } catch (error) {
      console.error('Error saving KPI assignments:', error)
      alert('Failed to save KPI assignments')
      return
    }
  }, [kpiAssignments, createAssignments, resetAdditionForm, onSave, t])

  const handleBranchSelect = useCallback(
    (branchName: string) => {
      setAdditionSelections((prev) => ({ ...prev, branch: branchName }))
      // Fetch metadata for the selected branch
      fetchMetadata(branchName)
    },
    [fetchMetadata]
  )

  const handleDateChange = useCallback(
    (newDate: DateRange | undefined, newDateFilterType: DateType) => {
      setAdditionSelections((prev) => ({
        ...prev,
        dateRange: newDate,
        dateFilterType: newDateFilterType,
      }))
    },
    []
  )

  const handleKpiTreeSelection = useCallback(
    (kpiId: string, categoryId: string) => {
      setSelectedKpiId(kpiId)
      console.log('Selected KPI:', kpiId, 'from category:', categoryId)
    },
    []
  )

  const handleUserSelection = useCallback((userId: string) => {
    setSelectedUserId(userId)
    console.log('Selected User:', userId)
  }, [])

  return (
    <div className="my-6">
      <div className="flex items-center gap-4 mb-4">
        <Button onClick={handleCancel}>
          <IconArrowLeft className="mr-2" />
          {t('common.buttons.back')}
        </Button>
        <h3 className="leading-none tracking-tight text-black-500 text-display-s">
          {t('targets.form.createGoalPlan')}
        </h3>
      </div>

      <div>
        {/* Form Section */}
        <Card>
          <CardContent className="p-3">
            {!metadata && loading ? (
              <Loading />
            ) : (
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                {/* Filter Controls */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label>{t('branchSelect.selectBranch')}</Label>
                    <SingleBranchSelect
                      selectedBranch={additionSelections.branch}
                      onSelectBranch={handleBranchSelect}
                    />
                  </div>

                  {/* Show KPI and User selectors only when metadata is loaded */}
                  {metadata && (
                    <>
                      <div className="flex flex-col gap-2">
                        <Label>{t('targets.form.kpi')}</Label>
                        <TreeSelector
                          data={kpiTreeData}
                          selectedId={selectedKpiId}
                          onSelect={handleKpiTreeSelection}
                          placeholder={t('targets.form.selectKpi')}
                          loading={loading}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label>User</Label>
                        <TreeSelector
                          data={userFlatData}
                          selectedId={selectedUserId}
                          onSelect={handleUserSelection}
                          placeholder="Select User"
                          loading={loading}
                          noTree
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label>{t('targets.table.date')}</Label>
                        <BasicDatePicker
                          value={additionSelections.dateRange}
                          dateFilterType={additionSelections.dateFilterType}
                          onChange={handleDateChange}
                          noMonths
                          yearsUnselectable
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label htmlFor="targetValue">
                          {t('targets.form.targetValue')}
                        </Label>
                        <Input
                          id="targetValue"
                          type="number"
                          min="0"
                          step="any"
                          value={targetValue === 0 ? '' : targetValue}
                          onChange={(e) =>
                            setTargetValue(
                              e.target.value === '' ? 0 : Number(e.target.value)
                            )
                          }
                          placeholder="0"
                          className="w-[200px]"
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label htmlFor="bonusAmount">Bonus Amount</Label>
                        <Input
                          id="bonusAmount"
                          type="number"
                          min="0"
                          step="any"
                          value={bonusAmount === 0 ? '' : bonusAmount}
                          onChange={(e) =>
                            setBonusAmount(
                              e.target.value === '' ? 0 : Number(e.target.value)
                            )
                          }
                          placeholder="0"
                          className="w-[200px]"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Action Button */}
                {metadata && (
                  <div className="flex gap-4 justify-center md:justify-start items-center">
                    <Button
                      onClick={handleAddKPI}
                      size="sm"
                      disabled={!isFormValid || loading}
                    >
                      <IconCirclePlus className="mr-2" />
                      {t('targets.form.addKpiGoal')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Table Section */}
        <div className="mt-6">
          <BasicTable
            data={kpiAssignments.map((assignment) => {
              return {
                id: assignment.id,
                branch: assignment.branchName,
                kpiName: assignment.kpiName,
                user: assignment.userName,
                date: `${assignment.year} Q${assignment.quarter}`,
                target: assignment.targetValue,
                bonus: assignment.bonusAmount,
                action: (
                  <Button
                    variant="destructive"
                    onClick={() => handleRemoveKPI(assignment.id)}
                    size="sm"
                  >
                    <IconTrash />
                  </Button>
                ) as any,
              }
            })}
            columns={[
              ...getKpiAddFormColumns(t),
              {
                accessorKey: 'action',
                header: '',
                type: 'text',
              },
            ]}
            title=""
            loading={false}
            enablePagination
            itemsPerPage={6}
            height={400}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={handleCancel}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            disabled={kpiAssignments.length === 0}
            type="submit"
            onClick={handleSave}
          >
            {t('common.buttons.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
